import { Navigate, NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { LayoutDashboard, CalendarDays, ListChecks, Sparkles, Settings, BarChart3, LogOut, UserCheck, Users } from "lucide-react";

const ALL_ITEMS = [
  { to: "/admin", label: "dashboard", icon: LayoutDashboard, tid: "admin-nav-dashboard", end: true, roles: ["admin"] },
  { to: "/admin/home", label: "dashboard", icon: LayoutDashboard, tid: "guruji-nav-dashboard", end: true, roles: ["guruji"] },
  { to: "/admin/quick-attendance", label: "quickAttendance", icon: UserCheck, tid: "admin-nav-quick", roles: ["admin", "guruji"] },
  { to: "/admin/bookings", label: "bookings", icon: ListChecks, tid: "admin-nav-bookings", roles: ["admin"] },
  { to: "/admin/calendar", label: "calendar", icon: CalendarDays, tid: "admin-nav-calendar", roles: ["admin"] },
  { to: "/admin/poojas", label: "poojas", icon: Sparkles, tid: "admin-nav-poojas", roles: ["admin"] },
  { to: "/admin/users", label: "users", icon: Users, tid: "admin-nav-users", roles: ["admin"] },
  { to: "/admin/reports", label: "reports", icon: BarChart3, tid: "admin-nav-reports", roles: ["admin"] },
  { to: "/admin/settings", label: "settings", icon: Settings, tid: "admin-nav-settings", roles: ["admin"] },
];

export function AdminLayout({ children, allowedRoles = ["admin"] }) {
  const { t } = useTranslation();
  const { user, loading, logout } = useAuth();
  const location = useLocation();

  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm opacity-70">Loading...</div>;
  if (!user || !allowedRoles.includes(user.role)) {
    if (user?.role === "helper") return <Navigate to="/helper" replace />;
    if (user?.role === "guruji") return <Navigate to="/admin/home" replace />;
    return <Navigate to="/admin/login" replace state={{ from: location }} />;
  }

  const items = ALL_ITEMS.filter((it) => it.roles.includes(user.role));

  return (
    <div className="min-h-screen flex bg-[color:var(--bg-2)]">
      <aside className="w-60 bg-white border-r border-[color:var(--border)] hidden md:flex flex-col">
        <div className="p-5 border-b border-[color:var(--border)]">
          <div className="font-heading text-xl">Acharya Guruji</div>
          <div className="text-[10px] uppercase tracking-[0.18em] opacity-70 mt-1">
            {user.role === "guruji" ? t("admin.gurujiConsole") : t("admin.adminConsole")}
          </div>
        </div>
        <nav className="flex-1 py-3 flex flex-col gap-0.5">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              data-testid={it.tid}
              className={({ isActive }) =>
                `mx-2 px-3 py-2 rounded-md text-sm flex items-center gap-3 transition-colors ${isActive ? "bg-[color:var(--bg-2)] text-[color:var(--brand)] font-medium" : "hover:bg-[color:var(--bg-2)]"}`}
            >
              <it.icon size={16} />
              {t(`admin.${it.label}`)}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-[color:var(--border)]">
          <div className="text-xs opacity-70 mb-1">{user.name}</div>
          <div className="text-[10px] opacity-50 mb-2">{user.email}</div>
          <button
            onClick={logout}
            data-testid="admin-logout-btn"
            className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm border border-[color:var(--border)] hover:bg-[color:var(--bg-2)]"
          >
            <LogOut size={14} /> {t("admin.logout")}
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="md:hidden bg-white border-b border-[color:var(--border)] sticky top-0 z-10">
          <div className="flex items-center justify-between p-3">
            <div className="font-heading text-lg">{user.role === "guruji" ? t("admin.gurujiConsole") : t("admin.adminConsole")}</div>
            <button onClick={logout} data-testid="admin-logout-btn-mobile" className="text-xs inline-flex items-center gap-1 border border-[color:var(--border)] rounded-md px-2 py-1">
              <LogOut size={12} /> {t("admin.logout")}
            </button>
          </div>
          <nav className="flex gap-1 overflow-x-auto px-2 pb-2 -mx-1">
            {items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                data-testid={`m-${it.tid}`}
                className={({ isActive }) =>
                  `flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs whitespace-nowrap ${isActive ? "bg-[color:var(--brand)] text-[#faf8f5]" : "bg-[color:var(--bg-2)]"}`}
              >
                <it.icon size={12} />
                {t(`admin.${it.label}`)}
              </NavLink>
            ))}
          </nav>
        </header>
        <main className="flex-1 p-4 md:p-8 overflow-auto">{children}</main>
      </div>
    </div>
  );
}
